// /home/coder/project/frontend/src/pages/trade/ToContractsCreate.tsx

import React, { useMemo } from 'react';
import { Form, Input, Spin, Alert, Typography, Divider, Row, Col, message, Space, Select, DatePicker, InputNumber } from 'antd';
import { SaveOutlined, RollbackOutlined } from '@ant-design/icons';
import { TbContract } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

// Import the relevant type for form data (subset of ToContracts)
import { ToContracts, Company, CorCountries, CorCurrencies } from '../../../types';
// Import the API functions
import { insertData, updateData } from '../../../services/api';
import FileUploadTool, { FileUploadToolHandle } from '../../../components/common/FileUploadTool';
import FileDownloadTool from '../../../components/common/FileDownloadTool';
// Import hooks for fetching related data (assuming these exist)
import { useGetCompanies,useGetCountries,useGetCurrencies } from '../../../hooks/genericHooks'; // Adjust path/name if needed
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

 const parseNumberLike = (raw: unknown): string => {
   if (raw === null || raw === undefined) return '';
   const s = String(raw).trim();
   if (!s) return '';

   // Normalize common user input variants:
   // - Allow comma as decimal separator: "10,5" -> "10.5"
   // - Allow comma as thousands separator when dot is used for decimals: "1,234.56" -> "1234.56"
   // - Allow spaces as grouping: "1 234,56" -> "1234.56"
   const noSpaces = s.replace(/\s+/g, '');

   const hasComma = noSpaces.includes(',');
   const hasDot = noSpaces.includes('.');

   if (hasComma && !hasDot) {
     // Treat the last comma as decimal separator; remove any earlier grouping commas.
     const lastComma = noSpaces.lastIndexOf(',');
     const intPart = noSpaces.slice(0, lastComma).replace(/,/g, '');
     const fracPart = noSpaces.slice(lastComma + 1).replace(/,/g, '');
     return `${intPart}.${fracPart}`;
   }

   // Default: remove grouping commas (and keep dot as decimal separator if present)
   return noSpaces.replace(/,/g, '');
 };

 const formatNumberWithGrouping = (raw: unknown): string => {
   const normalized = parseNumberLike(raw);
   if (!normalized) return '';

   const numeric = Number(normalized);
   if (!Number.isFinite(numeric)) return String(raw ?? '');

   const decimalPart = normalized.split('.')[1];
   const fractionDigits = decimalPart ? Math.min(decimalPart.length, 6) : 0;

   return new Intl.NumberFormat(undefined, {
     minimumFractionDigits: fractionDigits,
     maximumFractionDigits: fractionDigits,
   }).format(numeric);
 };

const RTbContract: React.FC<any> = (props) =>
  React.createElement(TbContract as unknown as React.ComponentType<any>, props) as React.ReactElement;

// Define the type for the form data (match fields in ToContracts, handle dates)
type ToContractFormData = Omit<ToContracts, 'id' | 'created_at' | 'updated_at' | 'signing_date' | 'start_date' | 'end_date'> & {
  signing_date: dayjs.Dayjs;
  start_date: dayjs.Dayjs;
  end_date?: dayjs.Dayjs | null;
};

// Define expected success response type (matches backend insertData response)
type InsertResponse = { ids: number[]; message: string; };

// Define expected success response type for update (align with previous edit page)
type UpdateResponse = { message: string; affectedRows: number };

type Mode = 'create' | 'edit';

interface Props {
  mode?: Mode;
  initialValues?: ToContracts | null;
  onCancel?: () => void;
}

const getContractMutationErrorMessage = (err: unknown, mode: Mode): string => {
  const defaultMessage =
    mode === 'edit'
      ? 'Kontrat kaydedilemedi.'
      : 'Kontrat olusturulamadi.';

  if (!err || typeof err !== 'object' || !('response' in err)) {
    return defaultMessage;
  }

  const axiosError = err as {
    message?: string;
    response?: {
      status?: number;
      data?: {
        error?: string;
      };
    };
  };

  const serverErrorMsg = axiosError.response?.data?.error;
  const statusCode = axiosError.response?.status;

  if (
    serverErrorMsg &&
    serverErrorMsg.includes('duplicate key value violates unique constraint') &&
    serverErrorMsg.includes('(reference_id)=')
  ) {
    const match = serverErrorMsg.match(/\(reference_id\)=\((.*?)\)/);
    const duplicateReferenceId = match ? match[1] : 'girilen referans';
    return `Bu referans numarasi zaten kayitli: "${duplicateReferenceId}". Lutfen farkli bir Reference ID girin.`;
  }

  if (serverErrorMsg) {
    return serverErrorMsg;
  }

  if (statusCode === 409) {
    return 'Ayni referans numarasina sahip baska bir kontrat zaten mevcut.';
  }

  return axiosError.message || defaultMessage;
};

const ToContractsCreate: React.FC<Props> = ({ mode = 'create', initialValues, onCancel }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<ToContractFormData>();
  const queryClient = useQueryClient();
  const fileUploadRef = React.useRef<FileUploadToolHandle | null>(null);

  // --- Queries for Select Options ---
  const { data: companiesResult, isLoading: isLoadingCompanies } = useGetCompanies();
  const { data: countriesResult, isLoading: isLoadingCountries } = useGetCountries();
  const { data: currenciesResult, isLoading: isLoadingCurrencies } = useGetCurrencies();

  const companies = useMemo(() => companiesResult?.data || [], [companiesResult]);
  const countries = useMemo(() => countriesResult?.data || [], [countriesResult]);
  const currencies = useMemo(() => currenciesResult?.data || [], [currenciesResult]);

  // Initialize form when editing
  React.useEffect(() => {
    if (mode !== 'edit' || !initialValues) return;
    form.setFieldsValue({
      ...initialValues,
      signing_date: initialValues.signing_date ? dayjs(initialValues.signing_date) : undefined,
      start_date: initialValues.start_date ? dayjs(initialValues.start_date) : undefined,
      end_date: initialValues.end_date ? dayjs(initialValues.end_date) : null,
      contract_value1: initialValues.contract_value1,
      contract_value2: initialValues.contract_value2,
    } as any);
  }, [mode, initialValues, form]);

  // --- Mutation for Creating Contract ---
  const {
    mutate: createContractMutate,
    isPending: isCreating,
    error: createError,
  } = useMutation<
    InsertResponse,
    Error,
    Omit<ToContracts, 'id' | 'created_at' | 'updated_at'> // Type for the actual payload sent
  >({
    mutationFn: (variables) => {
      console.log('Contract Payload being sent:', variables);
      return insertData('to_contracts', variables); // Use 'to_contracts' table name
    },
    onSuccess: (responseData, variables) => {
      if (responseData && Array.isArray(responseData.ids) && responseData.ids.length > 0) {
        const newId = responseData.ids[0];
        message.success(`Contract "${variables.contract_name}" (ID: ${newId}) created successfully!`);
        // Invalidate the cache for the contracts list to refetch
        queryClient.invalidateQueries({ queryKey: ['toContracts'] });
        queryClient.invalidateQueries({ queryKey: ['vwContractDetails'] });
        // Navigate back to the list page (adjust path if needed)
        navigate('/to/contracts');
      } else {
        message.error('Failed to get valid ID after creation.');
        console.error("Unexpected response format after contract creation:", responseData);
      }
    },
    onError: (err) => {
      message.error(getContractMutationErrorMessage(err, 'create'));
      console.error("Contract Creation Mutation Error:", err);
    },
  });

  // --- Mutation for Updating Contract ---
  const {
    mutate: updateContractMutate,
    isPending: isUpdating,
    error: updateError,
  } = useMutation<
    UpdateResponse,
    Error,
    Omit<ToContracts, 'id' | 'created_at' | 'updated_at'>
  >({
    mutationFn: (variables) => {
      const contractId = initialValues?.id;
      console.log('Contract Update Payload being sent:', variables);
      if (!contractId) throw new Error('Contract ID is missing');
      return updateData('to_contracts', contractId, variables);
    },
    onSuccess: (responseData, variables) => {
      const contractId = initialValues?.id;
      message.success(`Contract "${variables.contract_name}"${contractId ? ` (ID: ${contractId})` : ''} updated successfully!`);
      queryClient.invalidateQueries({ queryKey: ['toContracts'] });
      queryClient.invalidateQueries({ queryKey: ['vwContractDetails'] });
      if (contractId) {
        queryClient.invalidateQueries({ queryKey: ['toContract', contractId] });
      }
      navigate('/to/contracts');
    },
    onError: (err) => {
      message.error(getContractMutationErrorMessage(err, 'edit'));
      console.error('Contract Update Mutation Error:', err);
    },
  });

  // --- Form Submission Handler ---
  const onFinish = async (values: ToContractFormData) => {
    // Format dates and ensure optional fields are null if empty
    const payload: Omit<ToContracts, 'id' | 'created_at' | 'updated_at'> = {
      ...values,
      signing_date: values.signing_date.format('YYYY-MM-DD'),
      start_date: values.start_date.format('YYYY-MM-DD'),
      end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
      reference_id: values.reference_id as string,
      contract_currency2: values.contract_currency2 || null,
      description: values.description as string,
      // Ensure numeric values are sent correctly (as strings based on interface)
      contract_value1: String(values.contract_value1),
      contract_value2: (values.contract_value2 !== null && values.contract_value2 !== undefined && values.contract_value2 !== '') ? String(values.contract_value2) : null,
    };

    // Use reference_id (or contract_name as fallback) as the logical document reference
    const docRef = (payload.reference_id as string | null) || payload.contract_name;

    if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
      const up = await fileUploadRef.current.finalizeUpload(docRef);
      if (!up.success) {
        message.error(up.error || 'File upload failed');
        return;
      }
      (payload as any).upload_path = up.folderPath || null;
    }

    console.log('Form Values for Contract Submission:', payload);

    if (mode === 'edit') {
      updateContractMutate(payload);
    } else {
      createContractMutate(payload);
    }
  };

  // --- Cancel Handler ---
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/to/contracts');
    }
  };

  const isLoading = isLoadingCompanies || isLoadingCountries || isLoadingCurrencies || isCreating || isUpdating;

  // --- Render ---
  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <RTbContract size={20} style={{ marginRight: 8 }} />
              {mode === 'edit'
                ? `Edit Contract${initialValues?.id ? ` (ID: ${initialValues.id})` : ''}`
                : 'Create New Contract'}
            </Title>
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          {createError && (
            <Alert
              message="Hata"
              description={getContractMutationErrorMessage(createError, 'create')}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ status: 'active', contract_type: 'both', payment_type: 'lumpsum' }} autoComplete="off" >
            <Row gutter={[16, 0]}>
              {/* Row 1 */}
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Contract Name" name="contract_name" rules={[{ required: true, message: 'Please enter the contract name' }]} >
                  <Input placeholder="e.g., Main Construction Agreement" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Company" name="company_id" rules={[{ required: true, message: 'Please select the company' }]}>
                  <Select placeholder="Select company" loading={isLoadingCompanies} showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={companies.map((c: Company) => ({ value: c.id, label: c.name }))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Reference ID"
                  name="reference_id"
                  rules={[{ required: true, message: 'Please enter the reference ID' }]}
                >
                  <Input placeholder="Reference code" />
                </Form.Item>
              </Col>

              {/* Row 2 */}
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Signing Place" name="signing_place" rules={[{ required: true, message: 'Please select the signing country' }]}>
                  <Select placeholder="Select country" loading={isLoadingCountries} showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={countries.map((c: CorCountries) => ({ value: c.country_code, label: `${c.name} (${c.country_code})` }))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Signing Date" name="signing_date" rules={[{ required: true, message: 'Please select the signing date' }]}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Delivery Place" name="delivery_place" rules={[{ required: true, message: 'Please select the delivery country' }]}>
                  <Select placeholder="Select country" loading={isLoadingCountries} showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={countries.map((c: CorCountries) => ({ value: c.country_code, label: `${c.name} (${c.country_code})` }))} />
                </Form.Item>
              </Col>

              {/* Row 3 */}
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Start Date" name="start_date" rules={[{ required: true, message: 'Please select the start date' }]}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="End Date" name="end_date">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
               <Col xs={24} sm={12} md={8}>
                <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Please select the status' }]}>
                  <Select placeholder="Select status">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                    <Option value="terminated">Terminated</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Row 4 - Contract Values */}
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Contract Value 1" name="contract_value1" rules={[{ required: true, message: 'Please enter the primary contract value' }]}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="e.g., 10000.00"
                    precision={2}
                    stringMode
                    formatter={(value, info) => (info?.userTyping ? String(value ?? '') : formatNumberWithGrouping(value))}
                    parser={(value) => parseNumberLike(value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Currency 1" name="contract_currency1" rules={[{ required: true, message: 'Please select the primary currency' }]}>
                  <Select placeholder="Select currency" loading={isLoadingCurrencies} showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={currencies.map((c: CorCurrencies) => ({ value: c.currency_code, label: `${c.name} (${c.currency_code})` }))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Contract Value 2" name="contract_value2">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Optional secondary value"
                    precision={2}
                    stringMode
                    formatter={(value, info) => (info?.userTyping ? String(value ?? '') : formatNumberWithGrouping(value))}
                    parser={(value) => parseNumberLike(value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Currency 2" name="contract_currency2">
                  <Select placeholder="Select currency" loading={isLoadingCurrencies} allowClear showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={currencies.map((c: CorCurrencies) => ({ value: c.currency_code, label: `${c.name} (${c.currency_code})` }))} />
                </Form.Item>
              </Col>

              {/* Row 5 - Types */}
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Contract Type" name="contract_type" rules={[{ required: true, message: 'Please select the contract type' }]}>
                  <Select placeholder="Select type">
                    <Option value="service">Service</Option>
                    <Option value="goods">Goods</Option>
                    <Option value="both">Both</Option>
                    <Option value="client">Client</Option>
                    <Option value="partner">Partner</Option>
                    <Option value="public inst.">Public Inst.</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Payment Type" name="payment_type" rules={[{ required: true, message: 'Please select the payment type' }]}>
                  <Select placeholder="Select payment type">
                    <Option value="lumpsum">Lump Sum</Option>
                    <Option value="unitprice">Unit Price</Option>
                    <Option value="lumpsum/unitprice">Lump Sum/Unit Price</Option>
                    <Option value="non-disclosure">Non-Disclosure</Option>
                    <Option value="financing">Financing</Option>
                    <Option value="franchise">Franchise</Option>

                  </Select>
                </Form.Item>
              </Col>

              {/* Row 6 - Description */}
              <Col xs={24}>
                <Form.Item
                  label="Description"
                  name="description"
                  rules={[{ required: true, message: 'Please enter the contract description' }]}
                >
                  <TextArea rows={4} placeholder="Add relevant details or notes about the contract" />
                </Form.Item>
              </Col>
            </Row>
            <Divider />
            <Form.Item>
              <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <Button
                  action={mode === 'edit' ? 'edit' : 'add'}
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={mode === 'edit' ? isUpdating : isCreating}
                >
                  {mode === 'edit' ? 'Save Changes' : 'Create Contract'}
                </Button>
                <Button
                  icon={<RollbackOutlined />}
                  onClick={handleCancel}
                  disabled={mode === 'edit' ? isUpdating : isCreating}
                >
                  Cancel
                </Button>

                {mode === 'edit' && initialValues?.upload_path && String(initialValues.upload_path).trim() !== '' && (
                  <FileDownloadTool
                    contentPath={initialValues.upload_path}
                    buttonText={
                      <Button
                        type="primary"
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Attached Files
                      </Button>
                    }
                  />
                )}

                <FileUploadTool ref={fileUploadRef} buttonText="Upload" />
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </>
  );
};

export default ToContractsCreate;